// ============================================================================
// CMS Asset Picker Modal (M5 Wave 2)
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { ADMIN_COLORS, ADMIN_LAYOUT } from '../../../../../../generated/tokens/constants';
import { apiClient } from '../../../../../../services/axiosInstance';
import type { AssetPickerModalProps } from './types';
import type { CMSImage } from '../hooks/useCMSImages';
import { ImageCropModal } from '../../assets/components/ImageCropModal';
import { CropIcon } from '../../assets/components/CropIcon';

// ---------------------------------------------------------------------------
// Image type filter options
// ---------------------------------------------------------------------------
const IMAGE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Types' },
  { value: 'landing-hero', label: 'Hero' },
  { value: 'landing-inline', label: 'Inline' },
  { value: 'landing-og', label: 'OG' },
  { value: 'landing-icon', label: 'Icon' },
  { value: 'landing-mobile-hero', label: 'Mobile Hero' },
];

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function AssetPickerModal({
  isOpen,
  onClose,
  onSelect,
  currentImageId,
  defaultImageType,
}: AssetPickerModalProps) {
  // ---- State ----
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedImageId, setSelectedImageId] = useState<number | null>(
    currentImageId ?? null,
  );

  // Browse state
  const [images, setImages] = useState<CMSImage[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imageType, setImageType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Crop modal state
  const [cropTarget, setCropTarget] = useState<CMSImage | null>(null);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Reset on open ----
  useEffect(() => {
    if (isOpen) {
      setSelectedImageId(currentImageId ?? null);
      setTabIndex(0);
      setPage(1);
      setImages([]);
      setSearchTerm('');
      setImageType('');
      setHasError(false);
    }
  }, [isOpen, currentImageId]);

  // ---- Fetch images ----
  const fetchImages = useCallback(
    async (pageNum: number, type: string, append: boolean) => {
      setIsLoading(true);
      setHasError(false);
      try {
        const { data } = await apiClient.get(
          '/v1/admin/content-sections/cms-images/browse',
          {
            params: {
              page: pageNum,
              per_page: 40,
              image_type: type || undefined,
            },
          },
        );
        // GC envelope: setResults() lands at payload.results (object with nested results array)
        const wrapper = data?.payload?.results as
          | { results?: CMSImage[]; total?: number; per_page?: number; total_pages?: number }
          | undefined;
        const results = Array.isArray(wrapper?.results) ? wrapper!.results : [];
        // Normalize urls from [] (empty PHP array) to {} for each image
        for (const img of results) {
          if (Array.isArray(img.urls)) {
            (img as CMSImage).urls = {};
          }
        }
        const total = wrapper?.total ?? 0;
        const perPage = wrapper?.per_page ?? 40;
        const computedTotalPages = wrapper?.total_pages ?? (Math.ceil(total / perPage) || 1);

        if (append) {
          setImages((prev) => [...prev, ...results]);
        } else {
          setImages(results);
        }
        setTotalPages(computedTotalPages);
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // ---- Trigger fetch on filter change ----
  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    fetchImages(1, imageType, false);
  }, [isOpen, imageType, fetchImages]);

  // ---- Debounced search — treated same as filter for now ----
  useEffect(() => {
    if (!isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Re-fetch page 1 on search change (backend doesn't support search yet,
      // but we reset to keep UX consistent when search is wired)
      setPage(1);
      fetchImages(1, imageType, false);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // ---- Load more ----
  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchImages(nextPage, imageType, true);
  };

  // ---- Upload ----
  const handleUpload = async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post(
        `/v1/admin/content-sections/cms-images/upload?image_type=${encodeURIComponent(defaultImageType || 'landing-inline')}`,
        formData,
      );
      // Upload endpoint: setResults() puts the image data directly at payload.results
      const rawUploaded = data?.payload?.results;
      const uploaded = (rawUploaded?.image_id ? rawUploaded : undefined) as CMSImage | undefined;
      if (uploaded?.image_id) {
        setSelectedImageId(uploaded.image_id);
        // Switch to browse tab and refresh
        setTabIndex(0);
        setPage(1);
        fetchImages(1, imageType, false);
      }
    } catch {
      // upload failed — stay on tab
    } finally {
      setIsUploading(false);
    }
  };

  // ---- Drag events ----
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    // Reset so same file can be re-selected
    e.target.value = '';
  };

  // ---- Select + close ----
  const handleSelectConfirm = () => {
    if (selectedImageId != null) {
      onSelect(selectedImageId);
      onClose();
    }
  };

  // ---- Thumbnail URL helper ----
  const getThumbUrl = (img: CMSImage): string =>
    img.urls.medium ?? img.urls.small ?? img.urls.original ?? '';

  // ---- Dimensions label ----
  const getDimensions = (img: CMSImage): string | null => {
    if (img.width != null && img.height != null) {
      return `${img.width}×${img.height}`;
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay bg={ADMIN_COLORS.overlay} />
      <ModalContent bg={ADMIN_COLORS.card} gc-testing-id="asset-picker-modal">
        <ModalHeader color={ADMIN_COLORS.textPrimary}>Select Image</ModalHeader>
        <ModalCloseButton color={ADMIN_COLORS.textMuted} />

        <ModalBody p={0}>
          <Tabs
            index={tabIndex}
            onChange={setTabIndex}
            variant="enclosed"
            colorScheme="orange"
          >
            <TabList px={6} borderColor={ADMIN_COLORS.border}>
              <Tab
                color={ADMIN_COLORS.textSecondary}
                _selected={{ color: ADMIN_COLORS.textPrimary, borderColor: ADMIN_COLORS.brand }}
              >
                Browse Library
              </Tab>
              <Tab
                color={ADMIN_COLORS.textSecondary}
                _selected={{ color: ADMIN_COLORS.textPrimary, borderColor: ADMIN_COLORS.brand }}
              >
                Upload New
              </Tab>
            </TabList>

            <TabPanels>
              {/* ---- Browse Library ---- */}
              <TabPanel px={6} py={4}>
                {/* Filters */}
                <Flex gap={3} mb={4}>
                  <Input
                    gc-testing-id="asset-picker-search"
                    placeholder="Search images..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    {...ADMIN_LAYOUT.inputProps}
                    size="sm"
                    flex={1}
                  />
                  <Select
                    gc-testing-id="asset-picker-type-filter"
                    value={imageType}
                    onChange={(e) => setImageType(e.target.value)}
                    {...ADMIN_LAYOUT.inputProps}
                    size="sm"
                    w="180px"
                  >
                    {IMAGE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </Flex>

                {/* Error state */}
                {hasError && !isLoading && (
                  <Center py={10}>
                    <VStack spacing={2}>
                      <Text color={ADMIN_COLORS.textMuted} fontSize="sm">
                        Failed to load images
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => fetchImages(1, imageType, false)}
                      >
                        Retry
                      </Button>
                    </VStack>
                  </Center>
                )}

                {/* Loading initial */}
                {isLoading && images.length === 0 && (
                  <Center py={10}>
                    <Spinner color={ADMIN_COLORS.brand} />
                  </Center>
                )}

                {/* Empty state */}
                {!isLoading && !hasError && images.length === 0 && (
                  <Center py={10}>
                    <Text color={ADMIN_COLORS.textMuted} fontSize="sm">
                      No images found
                    </Text>
                  </Center>
                )}

                {/* Image grid */}
                {images.length > 0 && (
                  <>
                    <Grid templateColumns="repeat(4, 1fr)" gap={3}>
                      {images.map((img) => {
                        const isSelected = selectedImageId === img.image_id;
                        const hasCrop = img.crop_points != null;
                        const dims = getDimensions(img);
                        return (
                          <Box
                            key={img.image_id}
                            gc-testing-id={`asset-picker-image-${img.image_id}`}
                            cursor="pointer"
                            borderRadius="md"
                            overflow="hidden"
                            border="2px solid"
                            borderColor={
                              isSelected
                                ? ADMIN_COLORS.brand
                                : ADMIN_COLORS.border
                            }
                            bg={ADMIN_COLORS.surface}
                            onClick={() => setSelectedImageId(img.image_id)}
                            position="relative"
                            role="group"
                            _hover={{
                              borderColor: isSelected
                                ? ADMIN_COLORS.brand
                                : ADMIN_COLORS.hoverBorder,
                            }}
                            transition="border-color 0.15s"
                          >
                            {/* Checkmark overlay */}
                            {isSelected && (
                              <Center
                                position="absolute"
                                top={2}
                                right={2}
                                w="24px"
                                h="24px"
                                borderRadius="full"
                                bg={ADMIN_COLORS.brand}
                                zIndex={2}
                              >
                                <Icon
                                  as={CheckIcon}
                                  color="white"
                                  boxSize="12px"
                                />
                              </Center>
                            )}

                            {/* Crop badge — always visible when cropped */}
                            {hasCrop && (
                              <Flex
                                gc-testing-id={`asset-picker-crop-badge-${img.image_id}`}
                                position="absolute"
                                top={2}
                                left={2}
                                zIndex={2}
                                align="center"
                                gap="3px"
                                bg="rgba(0,0,0,0.75)"
                                color="white"
                                borderRadius="sm"
                                px={1.5}
                                py={0.5}
                                fontSize="10px"
                                fontWeight="600"
                                lineHeight={1}
                                pointerEvents="none"
                              >
                                <CropIcon boxSize="10px" />
                                Cropped
                              </Flex>
                            )}

                            {/* Edit crop button — visible on hover */}
                            <Center
                              gc-testing-id={`asset-picker-crop-btn-${img.image_id}`}
                              position="absolute"
                              bottom="38px"
                              right={2}
                              zIndex={2}
                              w="28px"
                              h="28px"
                              borderRadius="md"
                              bg="rgba(0,0,0,0.75)"
                              color="white"
                              opacity={0}
                              _groupHover={{ opacity: 1 }}
                              transition="opacity 0.15s"
                              cursor="pointer"
                              title={hasCrop ? 'Edit crop' : 'Crop image'}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCropTarget(img);
                              }}
                            >
                              <CropIcon boxSize="14px" />
                            </Center>

                            {/* Type badge */}
                            {img.image_type && (
                              <Badge
                                gc-testing-id={`asset-picker-type-badge-${img.image_id}`}
                                position="absolute"
                                top={2}
                                right={isSelected ? 9 : 2}
                                fontSize="2xs"
                                colorScheme="purple"
                                textTransform="uppercase"
                                zIndex={2}
                              >
                                {img.image_type.replace('landing-', '')}
                              </Badge>
                            )}

                            {/* Thumbnail */}
                            <Box h="120px" bg={ADMIN_COLORS.elevated}>
                              <Image
                                src={getThumbUrl(img)}
                                alt={img.filename ?? ''}
                                w="100%"
                                h="100%"
                                objectFit="contain"
                                loading="lazy"
                              />
                            </Box>

                            {/* Info */}
                            <Box px={2} py={1.5}>
                              <Text
                                fontSize="xs"
                                color={ADMIN_COLORS.textSecondary}
                                isTruncated
                              >
                                {img.filename ?? `Image #${img.image_id}`}
                              </Text>
                              {dims && (
                                <Text
                                  fontSize="10px"
                                  color={ADMIN_COLORS.textMuted}
                                >
                                  {dims}
                                </Text>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Grid>

                    {/* Load more */}
                    {page < totalPages && (
                      <Center mt={4}>
                        <Button
                          size="sm"
                          variant="outline"
                          isLoading={isLoading}
                          onClick={handleLoadMore}
                        >
                          Load More
                        </Button>
                      </Center>
                    )}
                  </>
                )}
              </TabPanel>

              {/* ---- Upload New ---- */}
              <TabPanel px={6} py={4}>
                <Box
                  gc-testing-id="asset-picker-upload-zone"
                  border="2px dashed"
                  borderColor={
                    isDragOver ? ADMIN_COLORS.brand : ADMIN_COLORS.border
                  }
                  borderRadius="lg"
                  p={10}
                  textAlign="center"
                  cursor="pointer"
                  bg={isDragOver ? ADMIN_COLORS.selectedAccentBg : ADMIN_COLORS.surface}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  transition="all 0.15s"
                >
                  {isUploading ? (
                    <VStack spacing={3}>
                      <Spinner color={ADMIN_COLORS.brand} size="lg" />
                      <Text color={ADMIN_COLORS.textSecondary} fontSize="sm">
                        Uploading...
                      </Text>
                    </VStack>
                  ) : (
                    <VStack spacing={3}>
                      <Text color={ADMIN_COLORS.textPrimary} fontSize="md" fontWeight="600">
                        Drop image here
                      </Text>
                      <Text color={ADMIN_COLORS.textMuted} fontSize="sm">
                        or click to browse — JPEG, PNG, WebP, GIF
                      </Text>
                      <Button size="sm" variant="outline" pointerEvents="none">
                        Choose File
                      </Button>
                    </VStack>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={ADMIN_COLORS.border}>
          <Button
            gc-testing-id="asset-picker-cancel-btn"
            variant="ghost"
            color={ADMIN_COLORS.textPrimary}
            mr={3}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            gc-testing-id="asset-picker-select-btn"
            bg={ADMIN_COLORS.brand}
            color="white"
            _hover={{ opacity: 0.9 }}
            isDisabled={selectedImageId == null}
            onClick={handleSelectConfirm}
          >
            Select
          </Button>
        </ModalFooter>
      </ModalContent>

      {/* Crop editor — opens on top of the picker */}
      {cropTarget && (
        <ImageCropModal
          imageId={cropTarget.image_id}
          imageUrl={cropTarget.original_source_url ?? cropTarget.urls.original ?? cropTarget.urls.large ?? cropTarget.urls.medium ?? ''}
          naturalWidth={cropTarget.width ?? 0}
          naturalHeight={cropTarget.height ?? 0}
          isOpen
          onClose={() => setCropTarget(null)}
          onSaved={() => {
            setCropTarget(null);
            // Refresh the grid to show updated crop badge
            fetchImages(1, imageType, false);
          }}
        />
      )}
    </Modal>
  );
}
